import React, {
  useMemo, useState, useContext, useEffect,
} from 'react';
import uuid from 'react-uuid';
import PropTypes from 'prop-types';
import {
  Button,
  Accordion,
  AccordionPanel,
  AccordionContent,
  ProgressCircular,
} from '@americanexpress/dls-react';
import {
  IconShow,
} from '@americanexpress/dls-icons';
import {
  base64ToBlobConversion,
  getMIMEType,
} from '../../utils/util';
import { fetchS3Data } from '../../utils/utilService';
import { extractMapResponseValueByKey } from '../../utils/conditionEvaluator';
import { ChildContext } from '../context';

const getDisplayValue = (value, localeText) => {
  const naValue = localeText?.naValue ?? '';

  if (Array.isArray(value)) {
    return value.length > 0 ? value.join(', ') : naValue;
  }

  if (typeof value === 'boolean') {
    return value ? localeText?.booleanTrue ?? '' : localeText?.booleanFalse ?? '';
  }

  if (value && typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value || '') || naValue;
};

const getRecordLabel = (record, index, options = {}) => {
  const primaryLabelKey = typeof options.primaryLabelKey === 'string'
    ? options.primaryLabelKey
    : '';
  const normalizedKeys = Array.isArray(options.preferredLabelKeys)
    ? options.preferredLabelKeys
    : [];
  const recordPrefix = options.recordPrefix || '';

  const primaryLabel = primaryLabelKey && typeof record?.[primaryLabelKey] === 'string'
    ? record[primaryLabelKey].trim()
    : '';

  if (primaryLabel) {
    return primaryLabel;
  }

  const dynamicLabel = normalizedKeys
    .map((key) => record?.[key])
    .find((value) => typeof value === 'string' && value.trim().length > 0);

  if (dynamicLabel) {
    return dynamicLabel;
  }

  return [recordPrefix, index + 1].filter(Boolean).join(' ').trim() || String(index + 1);
};

const asArray = (value) => (Array.isArray(value) ? value : []);

const toDomSafeId = (value) => String(value || '')
  .toLowerCase()
  .replace(/[^\d_a-z-]+/g, '-')
  .replace(/^-+|-+$/g, '');

const getNestedElements = (element) => [
  ...asArray(element.childElements),
  ...asArray(element.elements),
  ...asArray(element.elementContainer),
];

const NON_LABEL_VALUE_KEYS = ['verifiedTag', 'verifyButton', 'editAndRemoveButton', 'openButton'];

const stripHtml = (value) => {
  if (typeof value !== 'string') {
    return '';
  }

  return value.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
};

const getExpandableTableConfigs = (elements) => {
  const expandableTableConfigs = [];

  const traverse = (element) => {
    if (!element || typeof element !== 'object') return;

    if (element.fieldType === 'expandableTable') {
      expandableTableConfigs.push(element);
    }

    getNestedElements(element).forEach(traverse);
  };

  elements.forEach(traverse);
  return expandableTableConfigs;
};

const getRecordLabelKeysFromExpandableTable = (expandableTableConfig) => {
  if (!expandableTableConfig || typeof expandableTableConfig !== 'object') {
    return [];
  }

  const dynamicMappingKey = expandableTableConfig
    .rowExpansionCondition
    ?.dynamicMapping
    ?.propertyKey;

  const isAccordion = expandableTableConfig.viewMode === 'accordion';
  const keysSource = isAccordion
    ? expandableTableConfig.tableDynamicProperties?.accordionKeys
    : expandableTableConfig.tableDynamicProperties?.valueKeys;

  const displayKeys = asArray(keysSource)
    .filter((valueKey) => typeof valueKey === 'string' && !NON_LABEL_VALUE_KEYS.includes(valueKey));

  return [dynamicMappingKey, ...displayKeys].filter(Boolean);
};

const getPrimaryRecordLabelKeyFromExpandableTable = (expandableTableConfig) => {
  if (!expandableTableConfig || typeof expandableTableConfig !== 'object') {
    return '';
  }

  const isAccordion = expandableTableConfig.viewMode === 'accordion';
  const keysSource = isAccordion
    ? expandableTableConfig.tableDynamicProperties?.accordionKeys
    : expandableTableConfig.tableDynamicProperties?.valueKeys;

  const valueKeys = asArray(keysSource);
  const firstValueKey = valueKeys.find((valueKey) => typeof valueKey === 'string' && valueKey.trim());

  return firstValueKey || '';
};

const OpenRecordContent = ({ children, onOpen }) => {
  useEffect(() => {
    onOpen();
  }, [onOpen]);

  return children;
};

OpenRecordContent.propTypes = {
  children: PropTypes.node.isRequired,
  onOpen: PropTypes.func.isRequired,
};

/**
 * Dynamic FormSummarySection component that renders form data based on JSON configuration.
 */
const FormSummarySection = ({
  formElements,
  formSubmissionPayload,
  entityData,
  s3FileInfo,
  journeyId,
  formPageType,
  iformIntegrationUrls,
  onEditSection,
  locale,
  summaryValidationResult,
}) => {
  const contextVal = useContext(ChildContext);
  const [viewLoading, setViewLoading] = useState({});
  const [openSectionIds, setOpenSectionIds] = useState([]);
  const [openRecordIds, setOpenRecordIds] = useState({});
  const localeText = useMemo(() => (locale && typeof locale === 'object' ? locale : {}), [locale]);
  const sectionErrorsById = summaryValidationResult?.sectionErrorsById || {};
  const recordErrorsById = summaryValidationResult?.recordErrorsById || {};
  const erroredRecordIdsBySectionId = summaryValidationResult?.erroredRecordIdsBySectionId || {};

  const extractSections = useMemo(() => {
    if (!formElements || !Array.isArray(formElements)) {
      return [];
    }

    const summaryPages = formElements.filter(
      (node) => node && typeof node === 'object' && node.summary === true
    );

    if (summaryPages.length === 0) {
      return [];
    }

    const sections = [];

    const getSectionLabel = (node) => {
      if (!node) return null;
      if (node.section) return node.section;
      if (Array.isArray(node.childElements)) {
        const sectionChild = node.childElements.find((child) => child && child.section);
        if (sectionChild) return sectionChild.section;
      }
      return null;
    };

    const getNavSectionValue = (node) => node?.fieldDynamicDisplay?.mapCondition?.valueToCompare
      ?? node?.fieldDynamicDisplay?.multiKeyValueMappings?.['formSubmissionPayload.navDefaultSection']
      ?? null;

    const processNodeArray = (nodes, pageNo = null) => {
      if (!Array.isArray(nodes) || nodes.length === 0) return;

      const hasSectionMarkers = nodes.some((node) => getSectionLabel(node) !== null);

      if (hasSectionMarkers) {
        let currentSection = null;
        nodes.forEach((node) => {
          if (!node) return;
          const sectionLabel = getSectionLabel(node);
          if (sectionLabel) {
            const existing = sections.find((section) => section.label === sectionLabel);
            if (!existing) {
              const nestedSectionChild = node.section
                ? null
                : node.childElements?.find((child) => child && child.section);
              const navSection = getNavSectionValue(node) ?? getNavSectionValue(nestedSectionChild);
              currentSection = {
                id: sectionLabel,
                label: sectionLabel,
                navSection,
                pageNo,
                elements: [],
              };
              sections.push(currentSection);
            } else {
              currentSection = existing;
            }
          }

          if (currentSection) {
            currentSection.elements.push(node);
          }
        });
      } else {
        nodes.forEach((node) => {
          if (!node) return;
          const nextPage = typeof node.page === 'number' ? node.page : pageNo;
          if (node.childElements) processNodeArray(node.childElements, nextPage);
          if (node.elements) processNodeArray(node.elements, nextPage);
          if (node.elementContainer) processNodeArray(node.elementContainer, nextPage);
        });
      }
    };

    processNodeArray(summaryPages);
    return sections;
  }, [formElements]);

  const getFieldValue = (fieldPath) => {
    if (!fieldPath || !formSubmissionPayload || typeof formSubmissionPayload !== 'object') {
      return { exists: false, value: '' };
    }

    const normalizedPath = fieldPath.startsWith('formSubmissionPayload.')
      ? fieldPath.slice('formSubmissionPayload.'.length)
      : fieldPath;

    const pathCandidates = [normalizedPath];
    if (normalizedPath !== fieldPath) {
      pathCandidates.push(fieldPath);
    }

    const matchedValue = pathCandidates.reduce((result, pathCandidate) => {
      if (result.exists) {
        return result;
      }

      const pathParts = pathCandidate.split('.');
      let current = formSubmissionPayload;
      let exists = true;

      pathParts.forEach((part) => {
        if (!current || typeof current !== 'object' || !(part in current)) {
          exists = false;
          return;
        }
        if (exists) {
          current = current[part];
        }
      });

      return exists ? { exists: true, value: current } : result;
    }, { exists: false, value: '' });

    return matchedValue;
  };

  const normalizePayloadKey = (key) => (
    key.startsWith('formSubmissionPayload.') ? key.slice('formSubmissionPayload.'.length) : key
  );

  const hasHiddenCssClass = (element) => {
    const cssStyle = element?.cssProperties?.style || element?.cssProperties?.customStyle || '';
    return cssStyle.split(/\s+/).includes('hidden');
  };

  // Scope a fieldDynamicDisplay condition to only the keys in allowedKeySet.
  const scopeFieldDynamicDisplayByAllowedKeys = (fieldDynamicDisplay, allowedKeySet) => {
    if (!fieldDynamicDisplay || typeof fieldDynamicDisplay !== 'object') return null;

    const scopedDisplay = { ...fieldDynamicDisplay };

    if (Array.isArray(scopedDisplay.conditions)) {
      const scopedConditions = scopedDisplay.conditions
        .map((condition) => scopeFieldDynamicDisplayByAllowedKeys(condition, allowedKeySet))
        .filter(Boolean);
      if (scopedConditions.length === 0) return null;
      scopedDisplay.conditions = scopedConditions;
      return scopedDisplay;
    }

    if (scopedDisplay.propertyKey
      && !allowedKeySet.has(normalizePayloadKey(scopedDisplay.propertyKey))) {
      return null;
    }

    if (scopedDisplay.multiKeyValueMappings
      && typeof scopedDisplay.multiKeyValueMappings === 'object') {
      const filteredMappings = Object.entries(scopedDisplay.multiKeyValueMappings)
        .filter(([key]) => allowedKeySet.has(normalizePayloadKey(key)));
      if (filteredMappings.length === 0) return null;
      scopedDisplay.multiKeyValueMappings = Object.fromEntries(filteredMappings);
    }

    if (Array.isArray(scopedDisplay.mapCondition?.propertyKeys)) {
      const filteredPropertyKeys = scopedDisplay.mapCondition.propertyKeys
        .filter((key) => allowedKeySet.has(normalizePayloadKey(key)));
      if (filteredPropertyKeys.length === 0) return null;
      scopedDisplay.mapCondition = {
        ...scopedDisplay.mapCondition,
        propertyKeys: filteredPropertyKeys,
      };
    }

    return scopedDisplay;
  };

  // Evaluate a scoped dynamic display condition against payloadContext.
  // Returns true (visible) when there is no condition or the result is ambiguous.
  const isVisibleByScopedDisplay = (scopedDynamicDisplay, payloadContext) => {
    if (!scopedDynamicDisplay) return true;
    const result = extractMapResponseValueByKey(
      { fieldDynamicDisplay: scopedDynamicDisplay },
      'fieldDynamicDisplay',
      { formSubmissionPayload: payloadContext },
      {}
    );
    if (result === 'hide' || result === false) return false;
    return true;
  };

  // Collect all review fields from the element tree.
  // Step 1: skip subtrees with hidden CSS class.
  // Step 2: propagate scopedDynamicDisplay from any ancestor with removeIdsFromPayloadWhen.
  //         Fields under such an ancestor carry the scoped condition for use at render time.
  const collectFieldDescriptors = (elements) => {
    const fields = [];

    const traverse = (element, inheritedScopedDisplay = null) => {
      if (!element || typeof element !== 'object') return;

      if (hasHiddenCssClass(element)) return;

      let currentScopedDisplay = inheritedScopedDisplay;
      const removeWhenKeys = asArray(element.removeIdsFromPayloadWhen)
        .filter((k) => typeof k === 'string' && k.trim())
        .map(normalizePayloadKey);

      if (removeWhenKeys.length > 0 && element.fieldDynamicDisplay) {
        const scoped = scopeFieldDynamicDisplayByAllowedKeys(
          element.fieldDynamicDisplay,
          new Set(removeWhenKeys)
        );
        if (scoped) currentScopedDisplay = scoped;
      }

      if (element.fieldId && element.review) {
        const rawLabel = element.fieldLabel;
        const label = rawLabel && typeof rawLabel === 'object' ? rawLabel.label : rawLabel;
        fields.push({
          label: label || element.fieldId,
          fieldId: element.fieldId,
          scopedDynamicDisplay: currentScopedDisplay,
        });
        return;
      }

      getNestedElements(element).forEach((child) => traverse(child, currentScopedDisplay));
    };

    elements.forEach((el) => traverse(el, null));
    return fields;
  };

  const getUploadedDocumentConfig = (elements) => {
    let uploadedDocumentConfig = null;

    const traverse = (element) => {
      if (!element || typeof element !== 'object' || uploadedDocumentConfig) return;
      if (hasHiddenCssClass(element)) return;
      if (element.fieldType === 'attachFilesToPayloadV2' && element.fieldId) {
        const docCategoryMap = {};
        asArray(element.documents).forEach((documentGroup) => {
          asArray(documentGroup.documents).forEach((categoryRow) => {
            const mainCategoryLabel = categoryRow?.accordionLabel
              || stripHtml(categoryRow?.fieldLabel?.label);
            asArray(categoryRow.documents).forEach((docItem) => {
              if (docItem?.document_id) {
                docCategoryMap[docItem.document_id] = {
                  mainCategory: mainCategoryLabel,
                  childCategory: docItem.title || '',
                };
              }
            });
          });
        });

        uploadedDocumentConfig = {
          fieldId: element.fieldId,
          viewLabel: element.fieldLabel?.viewLabel,
          docCategoryMap,
        };
        return;
      }

      getNestedElements(element).forEach(traverse);
    };

    elements.forEach(traverse);
    return uploadedDocumentConfig;
  };

  // Only return record groups for the entity keys that belong to this section's expandable tables.
  // If no entity keys are provided, the section has no expandable table — return empty.
  const getSectionRecordGroups = (allowedEntityKeys = []) => {
    if (!entityData || typeof entityData !== 'object') return [];
    if (!Array.isArray(allowedEntityKeys) || allowedEntityKeys.length === 0) return [];

    const allowedKeySet = new Set(allowedEntityKeys);
    return Object.entries(entityData)
      .filter(([entityKey]) => allowedKeySet.has(entityKey))
      .filter(([, records]) => Array.isArray(records) && records.length > 0)
      .map(([entityKey, records]) => ({ entityKey, records }))
      .sort((groupA, groupB) => groupB.records.length - groupA.records.length);
  };

  const getFileData = async (fileId, fallbackFileName = '') => {
    const traceId = uuid();
    const paramsObj = {
      iformIntegrationUrls,
      cuid: contextVal?.cuid,
      pageType: formPageType,
      traceId,
      journeyId,
      requestPayload: {
        head: {
          traceId,
          span: 'iForm6513',
          journeyId,
        },
        body: {
          docKey: fileId,
        },
      },
    };

    const s3File = await fetchS3Data(paramsObj, 'file');
    const s3FileRes = await s3File.json();

    if (s3FileRes?.file) {
      const mimeType = getMIMEType(s3FileRes.file);
      const blobFile = base64ToBlobConversion(s3FileRes.file, mimeType);
      const anchor = window.document.createElement('a');
      anchor.href = window.URL.createObjectURL(new Blob([blobFile], { type: mimeType }));
      anchor.download = s3FileRes.fileName || fallbackFileName || 'document';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    }
  };

  const showUploadedDoc = async (fileInfo) => {
    setViewLoading((prev) => ({ ...prev, [fileInfo.fileId]: true }));
    try {
      await getFileData(fileInfo.fileId, fileInfo?.name || fileInfo?.documentTitle || 'document');
    } finally {
      setViewLoading((prev) => ({ ...prev, [fileInfo.fileId]: false }));
    }
  };

  const requestCollapsibleResize = () => {
    if (typeof window === 'undefined') {
      return;
    }

    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
      window.dispatchEvent(new Event('collapsibleDynamicResize'));
    }, 0);

    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
      window.dispatchEvent(new Event('collapsibleDynamicResize'));
    }, 300);
  };

  useEffect(() => {
    if (extractSections.length === 0) {
      setOpenSectionIds([]);
      return;
    }

    setOpenSectionIds((prev) => {
      if (prev.length > 0) {
        return prev;
      }

      return [String(extractSections[0].id)];
    });
  }, [extractSections]);

  useEffect(() => {
    // Open sections that have field-level errors OR record-level errors
    const sectionIdsFromFieldErrors = Object.keys(sectionErrorsById);
    const sectionIdsFromRecordErrors = Object.keys(erroredRecordIdsBySectionId);
    const allErroredSectionIds = [
      ...new Set([...sectionIdsFromFieldErrors, ...sectionIdsFromRecordErrors]),
    ];
    if (allErroredSectionIds.length === 0) return;

    setOpenSectionIds((prev) => [...new Set([
      ...prev,
      ...allErroredSectionIds.map((sectionId) => String(sectionId)),
    ])]);
  }, [sectionErrorsById, erroredRecordIdsBySectionId]);

  useEffect(() => {
    // Auto-open child record accordions using the explicit sectionId→recordIds map
    // (avoids fragile string-split to extract sectionId from recordId)
    if (Object.keys(erroredRecordIdsBySectionId).length === 0) return;

    setOpenRecordIds((prev) => {
      const next = { ...prev };
      Object.entries(erroredRecordIdsBySectionId).forEach(([sectionId, recordIds]) => {
        if (!next[sectionId]) next[sectionId] = [];
        recordIds.forEach((recordId) => {
          if (!next[sectionId].includes(recordId)) {
            next[sectionId].push(recordId);
          }
        });
      });
      return next;
    });
  }, [erroredRecordIdsBySectionId]);

  useEffect(() => {
    requestCollapsibleResize();
  }, [openSectionIds, openRecordIds]);

  const handleSectionToggle = (sectionId) => {
    setOpenSectionIds((prev) => {
      if (prev.includes(sectionId)) {
        return prev.filter((item) => item !== sectionId);
      }

      return [...prev, sectionId];
    });
  };

  const handleRecordToggle = (sectionId, recordId) => {
    setOpenRecordIds((prev) => {
      const currentOpenIds = prev[sectionId] || [];
      const nextOpenIds = currentOpenIds.includes(recordId)
        ? currentOpenIds.filter((item) => item !== recordId)
        : [...currentOpenIds, recordId];

      return {
        ...prev,
        [sectionId]: nextOpenIds,
      };
    });
  };

  const renderFieldsGrid = (fields) => {
    if (fields.length === 0) {
      return (
        <div style={{ fontSize: '14px', color: '#888' }}>
          {localeText?.noReviewFieldsMessage || ''}
        </div>
      );
    }

    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px 32px' }}>
        {fields.map((field) => (
          <div key={field.key} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span style={{
              fontSize: '12px', color: '#888', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.4px',
            }}
            >
              {field.label}
            </span>
            <span style={{ fontSize: '14px', color: '#333', wordBreak: 'break-word' }}>
              {field.value}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const renderUploadedDocuments = (uploadedDocumentConfig) => {
    if (!uploadedDocumentConfig || !Array.isArray(s3FileInfo)) {
      return null;
    }

    const uploadedDocuments = s3FileInfo.filter(
      (fileInfo) => fileInfo.fileFrom === uploadedDocumentConfig.fieldId
    );

    if (uploadedDocuments.length === 0) {
      return null;
    }

    return (
      <div style={{ marginTop: '24px' }}>
        <div
          style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#00175a',
            marginBottom: '12px',
          }}
        >
          {localeText?.documentsUploadedLabel || ''}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {uploadedDocuments.map((fileInfo) => {
            const docMeta = uploadedDocumentConfig.docCategoryMap?.[fileInfo.id] || {};
            const mainCategoryLabel = docMeta.mainCategory || '';
            const childCategoryLabel = docMeta.childCategory || fileInfo.documentTitle || '';

            return (
              <div
                key={fileInfo.fileId}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '16px',
                  padding: '12px 16px',
                  border: '1px solid #d0d0d0',
                  borderRadius: '4px',
                  backgroundColor: '#fff',
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <span style={{ fontSize: '14px', color: '#333', fontWeight: '600' }}>
                    {mainCategoryLabel || fileInfo.documentTitle || fileInfo.name}
                  </span>
                  {childCategoryLabel && (
                    <span style={{ fontSize: '13px', color: '#444' }}>
                      {childCategoryLabel}
                    </span>
                  )}
                  <span style={{ fontSize: '12px', color: '#666' }}>{fileInfo.name}</span>
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={Boolean(viewLoading[fileInfo.fileId])}
                  onClick={() => showUploadedDoc(fileInfo)}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                    {viewLoading[fileInfo.fileId] ? (
                      <ProgressCircular
                        size="sm"
                        determinate={false}
                        label={localeText?.viewLoadingLabel || 'Loading'}
                      />
                    ) : (
                      <IconShow size="xs" />
                    )}
                    {viewLoading[fileInfo.fileId]
                      ? localeText?.viewLoadingLabel || 'Loading'
                      : uploadedDocumentConfig.viewLabel || localeText?.viewDocumentLabel || ''}
                  </span>
                </Button>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderSection = (section) => {
    const sectionId = String(section.id);
    const sectionDomId = toDomSafeId(sectionId) || 'summary-section';
    const sectionAccordionId = `${sectionDomId}-accordion`;
    const recordsAccordionId = `${sectionDomId}-records-accordion`;
    const sectionErrors = sectionErrorsById[section.id] || [];
    const fieldDescriptors = collectFieldDescriptors(section.elements || []);
    const expandableTableConfigs = getExpandableTableConfigs(section.elements || []);
    const expandableTableConfigByShowId = expandableTableConfigs.reduce((accumulator, config) => {
      if (config?.showId) {
        accumulator[config.showId] = config;
      }
      return accumulator;
    }, {});
    const sectionEntityKeys = Object.keys(expandableTableConfigByShowId);
    const recordGroups = getSectionRecordGroups(sectionEntityKeys);
    const flattenedRecords = recordGroups.flatMap((group) => {
      const groupConfig = expandableTableConfigByShowId[group.entityKey];
      const groupLabelKeys = getRecordLabelKeysFromExpandableTable(groupConfig);
      const groupPrimaryLabelKey = getPrimaryRecordLabelKeyFromExpandableTable(groupConfig);

      return group.records.map((record, index) => ({
        entityKey: group.entityKey,
        groupIndex: index,
        primaryLabelKey: groupPrimaryLabelKey,
        labelKeys: groupLabelKeys,
        record,
      }));
    });
    const uploadedDocumentConfig = getUploadedDocumentConfig(section.elements || []);

    const payloadFields = fieldDescriptors
      .map((field) => {
        const { exists, value } = getFieldValue(field.fieldId);
        if (!exists) return null;
        // Step 3: apply conditional visibility from removeIdsFromPayloadWhen ancestor
        if (!isVisibleByScopedDisplay(
          field.scopedDynamicDisplay, formSubmissionPayload)
        ) return null;
        return {
          key: `${sectionId}-${field.fieldId}`,
          label: field.label,
          value: getDisplayValue(value, localeText),
        };
      })
      .filter(Boolean);

    const hasMultipleRecords = flattenedRecords.length > 1;
    const singleRecordFields = flattenedRecords.length === 1
      ? fieldDescriptors
        .filter(
          (field) => Object.prototype.hasOwnProperty.call(flattenedRecords[0].record, field.fieldId)
        )
        .filter((field) => isVisibleByScopedDisplay(
          field.scopedDynamicDisplay,
          { ...formSubmissionPayload, ...flattenedRecords[0].record }
        ))
        .map((field) => ({
          key: `${sectionId}-${field.fieldId}`,
          label: field.label,
          value: getDisplayValue(flattenedRecords[0].record[field.fieldId], localeText),
        }))
      : [];

    return (
      <div
        key={sectionId}
        style={{
          marginBottom: '12px',
          border: '1px solid #dfe3e8',
          borderRadius: 0,
          backgroundColor: '#fff',
          padding: '12px',
        }}
      >
        <style>{`
          #${sectionAccordionId} [id^="header-panel-"],
          #${sectionAccordionId} [id^="header-"] {
            width: 100%;
            display: flex;
            align-items: center;
            outline: none !important;
            box-shadow: none !important;
            border: none !important;
            border-bottom: none !important;
            text-decoration: none !important;
            background-image: none !important;
          }

          #${sectionAccordionId} [id^="header-panel-"]:hover,
          #${sectionAccordionId} [id^="header-panel-"]:active,
          #${sectionAccordionId} [id^="header-panel-"]:focus,
          #${sectionAccordionId} [id^="header-panel-"]:focus-visible,
          #${sectionAccordionId} [id^="header-panel-"] *:focus,
          #${sectionAccordionId} [id^="header-panel-"] *:focus-visible,
          #${sectionAccordionId} [id^="header-"]:hover,
          #${sectionAccordionId} [id^="header-"]:active,
          #${sectionAccordionId} [id^="header-"]:focus,
          #${sectionAccordionId} [id^="header-"]:focus-visible,
          #${sectionAccordionId} [id^="header-"] *:focus,
          #${sectionAccordionId} [id^="header-"] *:focus-visible {
            outline: none !important;
            box-shadow: none !important;
            border: none !important;
            border-bottom: none !important;
            border-color: transparent !important;
            text-decoration: none !important;
            background-image: none !important;
          }

          #${sectionAccordionId} [id^="header-panel-"] > div:last-of-type,
          #${sectionAccordionId} [id^="header-"] > div:last-of-type {
            flex: 1 1 auto;
            width: 100%;
            min-width: 0;
          }

          #${sectionAccordionId} [id^="header-panel-"] > div:last-of-type > div,
          #${sectionAccordionId} [id^="header-"] > div:last-of-type > div {
            width: 100%;
          }

          #${sectionAccordionId} [id^="header-panel-"]::after,
          #${sectionAccordionId} [id^="header-"]::after {
            display: none !important;
            content: none !important;
          }
        `}
        </style>
        <Accordion
          id={sectionAccordionId}
          isBordered={false}
          openPanelIds={openSectionIds.includes(sectionId) ? [sectionId] : []}
          onAccordionToggle={(event, panelId) => {
            handleSectionToggle(String(panelId));
          }}
          style={{ width: '100%', margin: 0 }}
        >
          <div style={{ position: 'relative' }}>
            <AccordionPanel
              id={sectionId}
              chevronPosition="start"
              className="flex"
              heading={(
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    paddingLeft: '12px',
                    lineHeight: '1.3',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {section.label}
                </div>
              )}
            />
          </div>
          <AccordionContent aria-labelledby={sectionId} style={{ width: '100%', paddingLeft: 0, paddingRight: 0 }}>
            <div style={{ padding: '12px 12px 16px', backgroundColor: '#fff' }}>
              {sectionErrors.length > 0 ? (
                <div
                  id={`summary-validation-${sectionId}`}
                  style={{
                    marginBottom: '16px',
                    padding: '12px 14px',
                    borderRadius: '4px',
                    border: '1px solid #c8102e',
                    backgroundColor: '#fff4f5',
                    color: '#8a1028',
                    fontSize: '13px',
                    lineHeight: '1.4',
                  }}
                >
                  {sectionErrors.map((errorText) => (
                    <div key={`${sectionId}-${errorText}`}>{errorText}</div>
                  ))}
                </div>
              ) : null}

              {hasMultipleRecords ? (
                <Accordion
                  id={recordsAccordionId}
                  isBordered={false}
                  shouldAllowMultipleExpanded={true}
                  openPanelIds={openRecordIds[sectionId] || []}
                  onAccordionToggle={(event, panelId) => {
                    handleRecordToggle(sectionId, String(panelId));
                  }}
                  style={{ width: '100%' }}
                >
                  <style>{`
                    #${recordsAccordionId} [id^="header-panel-"],
                    #${recordsAccordionId} [id^="header-"] {
                      width: 100%;
                      display: flex;
                      align-items: center;
                      outline: none !important;
                      box-shadow: none !important;
                      border: none !important;
                      border-bottom: none !important;
                      text-decoration: none !important;
                      background-image: none !important;
                    }

                    #${recordsAccordionId} [id^="header-panel-"]:hover,
                    #${recordsAccordionId} [id^="header-panel-"]:active,
                    #${recordsAccordionId} [id^="header-panel-"]:focus,
                    #${recordsAccordionId} [id^="header-panel-"]:focus-visible,
                    #${recordsAccordionId} [id^="header-panel-"] *:focus,
                    #${recordsAccordionId} [id^="header-panel-"] *:focus-visible,
                    #${recordsAccordionId} [id^="header-"]:hover,
                    #${recordsAccordionId} [id^="header-"]:active,
                    #${recordsAccordionId} [id^="header-"]:focus,
                    #${recordsAccordionId} [id^="header-"]:focus-visible,
                    #${recordsAccordionId} [id^="header-"] *:focus,
                    #${recordsAccordionId} [id^="header-"] *:focus-visible {
                      outline: none !important;
                      box-shadow: none !important;
                      border: none !important;
                      border-bottom: none !important;
                      border-color: transparent !important;
                      text-decoration: none !important;
                      background-image: none !important;
                    }

                    #${recordsAccordionId} [id^="header-panel-"] > div:last-of-type,
                    #${recordsAccordionId} [id^="header-"] > div:last-of-type {
                      flex: 1 1 auto;
                      width: 100%;
                      min-width: 0;
                    }

                    #${recordsAccordionId} [id^="header-panel-"] > div:last-of-type > div,
                    #${recordsAccordionId} [id^="header-"] > div:last-of-type > div {
                      width: 100%;
                    }

                    #${recordsAccordionId} [id^="header-panel-"]::after,
                    #${recordsAccordionId} [id^="header-"]::after {
                      display: none !important;
                      content: none !important;
                    }
                  `}
                  </style>
                  {flattenedRecords.map(({
                    entityKey,
                    groupIndex,
                    primaryLabelKey,
                    labelKeys,
                    record,
                  }, index) => {
                    const recordId = `${sectionId}-${entityKey}-record-${groupIndex}`;
                    const isRecordOpen = (openRecordIds[sectionId] || []).includes(recordId);
                    const recordErrors = recordErrorsById[recordId] || [];
                    const recordFields = fieldDescriptors
                      .filter(
                        (field) => Object.prototype.hasOwnProperty.call(record, field.fieldId)
                      )
                      .filter((field) => isVisibleByScopedDisplay(
                        field.scopedDynamicDisplay,
                        { ...formSubmissionPayload, ...record }
                      ))
                      .map((field) => ({
                        key: `${recordId}-${field.fieldId}`,
                        label: field.label,
                        value: getDisplayValue(record[field.fieldId], localeText),
                      }));

                    return [
                      <AccordionPanel
                        key={`${recordId}-panel`}
                        id={recordId}
                        chevronPosition="start"
                        heading={(
                          <div style={{
                            display: 'flex',
                            alignItems: 'self-start',
                            paddingLeft: '12px',
                            whiteSpace: 'nowrap',
                            fontWeight: 400,
                          }}
                          >
                            {getRecordLabel(record, index, {
                              primaryLabelKey,
                              preferredLabelKeys: labelKeys,
                              recordPrefix: localeText?.recordPrefixLabel || '',
                            })}
                          </div>
                        )}
                      />,
                      <AccordionContent
                        key={`${recordId}-content-${isRecordOpen ? 'open' : 'closed'}`}
                        aria-labelledby={recordId}
                      >
                        <OpenRecordContent onOpen={requestCollapsibleResize}>
                          <div
                            style={{
                              padding: '16px 20px',
                              backgroundColor: '#fff',
                              borderTop: '1px solid #ececec',
                              marginBottom: index === flattenedRecords.length - 1 ? 0 : '8px',
                            }}
                          >
                            {recordErrors.length > 0 ? (
                              <div
                                id={`record-validation-${recordId}`}
                                style={{
                                  marginBottom: '16px',
                                  padding: '12px 14px',
                                  borderRadius: '4px',
                                  border: '1px solid #c8102e',
                                  backgroundColor: '#fff4f5',
                                  color: '#8a1028',
                                  fontSize: '13px',
                                  lineHeight: '1.4',
                                }}
                              >
                                {recordErrors.map((errorText) => (
                                  <div key={`${recordId}-${errorText}`}>{errorText}</div>
                                ))}
                              </div>
                            ) : null}
                            {renderFieldsGrid(recordFields)}
                          </div>
                        </OpenRecordContent>
                      </AccordionContent>,
                    ];
                  })}
                </Accordion>
              ) : null}

              {!hasMultipleRecords && flattenedRecords.length === 1
                ? renderFieldsGrid(singleRecordFields)
                : null}

              {!hasMultipleRecords && flattenedRecords.length === 0
                ? renderFieldsGrid(payloadFields)
                : null}

              {renderUploadedDocuments(uploadedDocumentConfig)}

              {onEditSection && (
                <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      onEditSection(section.navSection ?? null, section.pageNo);
                    }}
                  >
                    {localeText?.editButtonLabel || ''}
                  </Button>
                </div>
              )}
            </div>
          </AccordionContent>
        </Accordion>
      </div>
    );
  };

  if (!formElements || formElements.length === 0) {
    return <div style={{ padding: '24px' }}>{localeText?.noFormDataMessage || ''}</div>;
  }

  if (extractSections.length === 0) {
    return <div style={{ padding: '24px' }}>{localeText?.noSectionsMessage || ''}</div>;
  }

  return (
    <div style={{ marginBottom: '24px' }}>
      {extractSections.map((section) => renderSection(section))}
    </div>
  );
};

FormSummarySection.propTypes = {
  formElements: PropTypes.arrayOf(
    PropTypes.shape({
      // eslint-disable-next-line react/forbid-prop-types -- existing dynamic element schema
      childElements: PropTypes.array,
    })
  ),
  entityData: PropTypes.shape({}),
  formSubmissionPayload: PropTypes.shape({}),
  s3FileInfo: PropTypes.arrayOf(PropTypes.shape({})),
  journeyId: PropTypes.string,
  formPageType: PropTypes.string,
  iformIntegrationUrls: PropTypes.string,
  locale: PropTypes.shape({}),
  onEditSection: PropTypes.func,
  summaryValidationResult: PropTypes.shape({
    hasErrors: PropTypes.bool,
    sectionErrorsById: PropTypes.shape({}),
    erroredSectionIds: PropTypes.arrayOf(PropTypes.string),
    recordErrorsById: PropTypes.shape({}),
    erroredRecordIds: PropTypes.arrayOf(PropTypes.string),
    erroredRecordIdsBySectionId: PropTypes.shape({}),
  }),
};

FormSummarySection.defaultProps = {
  formElements: [],
  entityData: null,
  formSubmissionPayload: null,
  s3FileInfo: [],
  journeyId: '',
  formPageType: '',
  iformIntegrationUrls: '',
  locale: null,
  onEditSection: null,
  summaryValidationResult: null,
};

export default FormSummarySection;
